/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    object, func, string, array,
} from 'prop-types';
import mbpLogger from 'mbp-logger';
import { isBefore } from 'date-fns';
import { FormControlLabel, Radio, RadioGroup } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import { useUIDSeed } from 'react-uid';
import { getFeatureFlag } from '../../../../../../state/ducks/App/ducks/Config/Config-Selectors';
import { dateStringReformat } from '../../../../../components/GraphqlComponents/GraphqlProductPage/Partials/common/commonProductPageHelper';
import ProductImageCarousel from './ProductImageCarousel';
import { isEmpty } from '../../../utils/object';
import { getContactId, getMembershipType } from '../../../../../../state/ducks/Member/ducks/Common/Common-Selectors';
import { getBrandName } from '../../../../../../state/ducks/App/ducks/Brand/Brand-Selectors';
import { getPriceDiffLabel, limitTextWithWordBoundary, productAvailability } from '../helper';
import { VariantLoader } from './Loader';
import { getSkuWithoutBrand } from '../../../../../helpers/common/helper';
import { trackEvent } from '../../../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';
import EventDispatcher from '../../../state/reducers/EventDispatcher';

const useStyles = makeStyles({
    contentContainerWrapper: {
        width: '100%',
        padding: '0 !important',
        margin: '50px 0',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        '@media screen and (max-width: 375px)': {
            borderLeft: 'none',
            borderRight: 'none',
        },
        '& .slick-list': {
            paddingLeft: '5px !important',
        },
        '& ._content': {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '0 12px 26px !important',
            '& > h2': {
                fontSize: '20px',
                fontStyle: 'normal',
                fontWeight: '700',
                lineHeight: '32px', /* 160% */
                letterSpacing: '0.2px',
                margin: '22px 0 8px 0',
                '@media screen and (max-width: 1024px)': {
                    textAlign: 'center',
                    fontSize: '18px',
                    lineHeight: '26px',
                    letterSpacing: '0.18px',
                },
                '@media screen and (max-width: 375px)': {
                    fontSize: '16px',
                },
            },
            '& > p': {
                fontSize: '16px',
                fontStyle: 'normal',
                fontWeight: '400',
                lineHeight: '24px', /* 150% */
                margin: '0 0 18px 0',
                textAlign: 'center',
                maxWidth: '390px',
                '@media screen and (max-width: 1024px)': {
                    fontSize: '14px',
                    fontStyle: 'normal',
                    lineHeight: '20px',
                    letterSpacing: '0.14px',
                },
                '@media screen and (max-width: 425px)': {
                    textAlign: 'start',
                },
            },
        },
    },
    contentContainer: {
        width: '100%',
        maxWidth: '300px',
        textAlign: 'left',
        '@media screen and (max-width: 425px)': {
            maxWidth: '100%',
        },
    },
    radioContainer: {
        display: 'flex',
        alignItems: 'center',
        marginBottom: '18px',
        '& label': {
            flex: '1',
            '& span:nth-last-child(1)': {
                fontSize: '16px',
                '@media screen and (max-width: 425px)': {
                    fontSize: '14px',
                },
            },
        },
        '& .MuiFormControlLabel-root': {
            marginLeft: '-4px',
        },
    },
    skuLabelAmount: {
        fontWeight: '500',
        marginLeft: '10px',
        fontSize: '16px',
        lineHeight: '24px',
        '@media screen and (max-width: 425px)': {
            fontSize: '14px',
        },
    },
    samePrice: {
        textAlign: 'right',
        fontSize: '16px',
        fontStyle: 'normal',
        fontWeight: '400',
        lineHeight: '24px', /* 150% */
        color: '#555555',
        '@media screen and (max-width: 425px)': {
            fontSize: '14px',
        },
    },
    radio: {
        color: '#52297E',
        padding: '0 10px 0 0',
        '&$checked': {
            color: '#52297E',
        },
    },
    checked: {},
    readMore: {
        fontSize: '16px',
        fontStyle: 'normal',
        fontWeight: '400',
        lineHeight: '24px',
        color: '#53237F',
        cursor: 'pointer',
        textDecoration: 'underline',
        textTransform: 'capitalize',
        '@media screen and (max-width: 425px)': {
            fontSize: '14px',
        },
    },
    centerTitle: {
        textAlign: 'center',
    },
    btnContainer: {
        marginTop: '13px',
        display: 'flex',
        justifyContent: 'space-between',
        '@media screen and (max-width: 400px)': {
            flexDirection: 'column',
            alignItems: 'center',
        },
        '& button': {
            display: 'flex',
            background: 'transparent',
            border: '1px solid #53237F',
            borderRadius: '3px',
            color: '#53237F',
            padding: '8px 20px',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '10px',
            textAlign: 'center',
            fontSize: '14px',
            fontStyle: 'normal',
            fontWeight: '700',
            lineHeight: '20px', /* 142.857% */
            letterSpacing: '0.56px',
            cursor: 'pointer',
            boxShadow: 'none',
            '@media screen and (max-width: 400px)': {
                width: '100%',
            },
        },
        '& .select_btn': {
            background: '#53237F !important',
            color: '#fff !important',
            '@media screen and (max-width: 400px)': {
                marginBottom: '12px',
            },
        },
    },
});

function Variants({
    product, content, productPrice, closeVariants, getPriceRules,
    subscription, getImagePath, swapSubscription, dynamicPriceList, productSkuPrices,
}) {
    const dispatch = useDispatch();
    const [sortedSkus, setSortedSkus] = useState({});
    const contactId = useSelector((state) => getContactId(state));
    const brandName = useSelector(getBrandName);
    const [readMore, setReadMore] = useState(false);
    const [selectedSku, setSelectedSku] = useState({});
    const [selectedPrice, setSelectedPrice] = useState('');
    const [isProductAvailable, setIsProductAvailable] = useState('');
    const [checkProductAvailability, setCheckProductAvailability] = useState(false);
    const membershipType = useSelector(getMembershipType);

    const isPDPSkuRankingEnabled = useSelector(getFeatureFlag('is-pdp-sku-ranking-enabled'));
    const recipientZipCode = subscription?.recipient?.[0]?.zipCode || subscription?.recipient?.[0]?.zipcode;

    const classes = useStyles();
    const seed = useUIDSeed();

    /*
    * @description determines if sku rankings are all valid and should be used for sku sorting. If there are duplicates or a sku is unranked (defaulted to 99) then the skus are not valid. Rank of zero is valid at the moment.
    * @param {array} product skus
    * @return {bool} validity of skus
    */
    const skusHaveDuplicateRanksOrAreUnranked = (skus) => {
        const ranks = new Set();
        return skus.some((sku) => {
            if (sku.rank === 99) {
                return true;
            }
            return ranks.size === ranks.add(sku.rank).size;
        });
    };

    // Filters out inactive skus based on 'published' and 'displayEndDate' attributes ('Display to Customer' and 'End Date' in PIM)
    const filteredSkus = (productSkus) => productSkus.filter((sku) => {
        const skuFormattedEndDate = dateStringReformat(sku.availability?.displayEndDate);
        const skuDateObject = skuFormattedEndDate ? (new Date(skuFormattedEndDate)) : '';
        const skuEndDateIsInPast = isBefore(skuDateObject, new Date());

        const isPublished = sku.availability?.published;
        const isSubscriptionProduct = sku.subscriptions?.type;

        return isPublished && !skuEndDateIsInPast && isSubscriptionProduct;
    });

    const getFilteredSkus = (productSkus) => {
        const skuRanking = content?.entries?.[0]?.sku_section?.sku_ranking;
        let filtered = filteredSkus(productSkus).slice(0);
        if (isPDPSkuRankingEnabled && !skusHaveDuplicateRanksOrAreUnranked(productSkus)) return filtered.sort((a, b) => Number(a.rank) - Number(b.rank));
        switch (skuRanking) {
            default:
                filtered = filtered.sort((a, b) => Number(b.prices?.[0]?.value) - Number(a.prices?.[0]?.value));
                break;
            case 'low to high':
                filtered = filtered.sort((a, b) => Number(a.prices?.[0]?.value) - Number(b.prices?.[0]?.value));
                break;
            case 'next available date':
                filtered = filtered.sort((a, b) => new Date(dateStringReformat(a.availability.earliestShipDate)).getTime() - new Date(dateStringReformat(b.availability.earliestShipDate)).getTime());
                break;
        }
        return filtered;
    };

    const selectVariant = (sku, price) => {
        setSelectedSku(sku);
        setSelectedPrice(price);
    };

    const handleRadioChange = (e) => {
        setIsProductAvailable('');

        const skuId = e.target.value;
        if (!skuId) return;

        const sku = sortedSkus.find((val) => val.id === skuId);
        const [currentSkuRetailPrice, currentSkuActualPriceDetails] = dynamicPriceList(sku);
        selectVariant(sku, currentSkuActualPriceDetails?.hasSale ? currentSkuActualPriceDetails?.value : currentSkuRetailPrice);
    };

    useEffect(() => {
        if (!product?.productSkus) return;
        const filtered = getFilteredSkus(product.productSkus);
        const selectedVariant = filtered.find((prod) => {
            const [, currentSkuActualPriceDetails] = dynamicPriceList(prod);
            return Number(productPrice) === Number(currentSkuActualPriceDetails?.value);
        }) || filtered?.[0] || {};

        const priceRules = getPriceRules(selectedVariant?.id);
        const updateSku = { ...selectedVariant, priceRules };
        const [currentSkuRetailPrice, currentSkuActualPriceDetails] = dynamicPriceList(selectedVariant);
        selectVariant(updateSku, currentSkuActualPriceDetails?.hasSale ? currentSkuActualPriceDetails?.value : currentSkuRetailPrice);
        setSortedSkus(filtered);
    }, [product.partNumber]);

    const renderRadio = (sku) => {
        const [currentSkuRetailPrice, currentSkuActualPriceDetails] = dynamicPriceList(sku);

        if (currentSkuActualPriceDetails?.hasSale && currentSkuRetailPrice === currentSkuActualPriceDetails?.value) {
            mbpLogger.logError({
                appName: process.env.npm_package_name,
                component: 'Variants.js',
                message: `Error with subscription prices on radio when customer selects subscription option, Display price ${currentSkuRetailPrice} is same as Sale Price ${currentSkuActualPriceDetails?.value} }`,
            });
        }

        return (
            <div key={seed(sku)} className={classes.radioContainer}>
                <FormControlLabel value={sku.id} label={sku.name} control={<Radio classes={{ root: classes.radio, checked: classes.checked }} />} />
                {selectedSku.id === sku.id && <span className={classes.samePrice}>{getPriceDiffLabel(Number(productPrice), Number(currentSkuActualPriceDetails?.value))}</span>}
                <span className={classes.skuLabelAmount}>${currentSkuActualPriceDetails?.hasSale ? currentSkuActualPriceDetails?.value : currentSkuRetailPrice}</span>
            </div>
        );
    };

    const visibleText = readMore ? product.shortDescription : limitTextWithWordBoundary(product.shortDescription, 85);

    const handleCancelVariant = () => {
        closeVariants({});
        dispatch(trackEvent({
            eventCategory: 'Subscriptions Management Portal',
            eventAction: 'click - Change Product',
            eventLabel: 'Cancel',
        }));
    };

    const handleSwap = async () => {
        setCheckProductAvailability(true);
        setIsProductAvailable('');
        if (!(await productAvailability(subscription, getSkuWithoutBrand(selectedSku.id), recipientZipCode, membershipType, setIsProductAvailable, setCheckProductAvailability))) return;

        const dynamicPrices = productSkuPrices?.find((x) => x.id === selectedSku.id)?.prices || [];
        // TO DO: need to also send the passport bundle data for final price calculations
        const data = {
            productId: getSkuWithoutBrand(selectedSku.id),
            productName: product.name,
            productPrice: selectedPrice,
            contactId,
            productImageCategory: getImagePath(product.image, 'c'),
            productImageThumbnail: getImagePath(product.image, 't'),
            partNumber: selectedSku.id,
            brandName,
            prices: dynamicPrices,
        };
        dispatch(trackEvent({
            eventCategory: 'Subscriptions Management Portal',
            eventAction: 'click - Change Product',
            eventLabel: `Select This Product - ${product.name}`,
        }));
        swapSubscription({ ruleId: subscription.ruleId, data });
        dispatch(EventDispatcher.clearSwapSubscription());
    };

    return (
        <div className={classes.contentContainerWrapper} id="product_slider">
            {!isEmpty(product) && !isEmpty(selectedSku) && <ProductImageCarousel product={product} selectedSku={selectedSku} />}
            <div className="_content">
                <h2>{product.name}</h2>
                <p>
                    {visibleText} &nbsp;
                    <span
                        role="button"
                        tabIndex={0}
                        onKeyPress={() => { }}
                        className={classes.readMore}
                        onClick={() => setReadMore((prev) => !prev)}
                    >
                        {readMore ? ' Read less' : ' Read more'}
                    </span>
                </p>
                <div className={classes.contentContainer}>
                    {!sortedSkus?.length && <div className={classes.centerTitle}><h3>No Subscriptions Available</h3></div>}
                    {!productSkuPrices?.length && <VariantLoader />}
                    {!isEmpty(selectedSku) && product?.productSkus && !!productSkuPrices.length && sortedSkus.length && (
                        <RadioGroup
                            name="product-radio-buttons-group"
                            value={selectedSku.id}
                            onChange={(e) => handleRadioChange(e)}
                        >
                            {sortedSkus.map((sku) => renderRadio(sku))}
                        </RadioGroup>
                    )}
                    {isProductAvailable === 'not_available' && <div style={{ color: 'red', padding: '10px 0', textAlign: 'center' }}>We are sorry, the product that you have selected is not available. Please choose another.</div>}
                    <div className={classes.btnContainer}>
                        <button disabled={!productSkuPrices?.length || checkProductAvailability} type="button" className="select_btn" onClick={handleSwap}>Select This Product</button>
                        <button type="button" onClick={handleCancelVariant}>Cancel</button>
                    </div>
                </div>
            </div>
        </div>
    );
}

Variants.defaultProps = {
    content: {},
};

Variants.propTypes = {
    content: object,
    product: object.isRequired,
    productPrice: string.isRequired,
    subscription: object.isRequired,
    getImagePath: func.isRequired,
    closeVariants: func.isRequired,
    swapSubscription: func.isRequired,
    productSkuPrices: array.isRequired,
    dynamicPriceList: func.isRequired,
    getPriceRules: func.isRequired,
};

export default Variants;
